import { fromJS } from 'immutable';
import { createSelector } from 'reselect';
import { getSearch } from 'selectors/settingsSelectors';
import { getProjects, getTeamMaps } from './projectsSelectors';
import { getCredentialListDataset, getCredentialListFilters } from '../ui/credentialListSelectors';
import { getUsers } from 'selectors/entities/userSelectors';

// Returns all credentials from the state filtered by credentialSearch if needed
export const getCredentials = (state, props = {}) =>
  state
    .getIn(['entities', 'credential', 'credentials'])
    .filter((credential) => (props.projectId ? credential.get('projectId') === parseInt(props.projectId) : true));

export const getCredentialGroupsFromState = (state) => state.getIn(['entities', 'credential', 'credentialGroups']);

// Returns all credential groups from the state
export const getCredentialGroups = (state, props) =>
  state.getIn(['entities', 'credential', 'credentialGroups']).filter((group) => {
    if (props && props.search) {
      const fullHeader = group.get('name') + group.get('description');
      return fullHeader?.toLowerCase()?.indexOf(props.search?.toLowerCase()) !== -1;
    } else {
      return true;
    }
  });

// Returns credential by specified credentialId
export const getCredentialById = (state, { credentialId }) =>
  state.getIn(['entities', 'credential', 'credentials', credentialId]);

// Returns credential group by specified groupId
export const getCredentialGroupById = (state, { groupId }) =>
  state.getIn(['entities', 'credential', 'credentialGroups', groupId]);

// Returns credentials filtered by specified groupId
export const getCredentialsByGroup = createSelector([getCredentialGroupById, getCredentials], (group, credentials) => {
  if (group) {
    return credentials.filter((credential) => credential.get('groupId') === group.get('groupId'));
  }
  return null;
});

// Returns credentials contained in the credential list dataset
export const getCredentialByDataset = createSelector(
  [getCredentials, getCredentialListDataset],
  (credentials, dataset) => {
    return credentials
      .filter((credential) => dataset.indexOf(credential.get('accessId')) !== -1)
      .sort((a, b) => {
        const aIndex = dataset.indexOf(a.get('accessId'));
        const bIndex = dataset.indexOf(b.get('accessId'));

        return aIndex > bIndex ? 1 : -1;
      });
  },
);

// Returns credentials filtered by current credential list dataset and filters
export const getFilteredCredentials = createSelector(
  [getCredentialByDataset, getCredentialListFilters, getSearch],
  (credentials, filters, search) => {
    if (filters.get('category') && filters.get('category') !== 'all') {
      credentials = credentials.filter((credential) => {
        if (filters.get('category') === 'without') {
          return !credential.get('groupId');
        }
        return credential.get('groupId') === filters.get('category');
      });
    }
    switch (filters.get('filter')) {
      case 'favorite':
        credentials = credentials.filter(
          (credential) => credential.get('status') !== 'moderated' && credential.get('favorite') === true,
        );
        break;
      case 'common':
        credentials = credentials.filter(
          (credential) => credential.get('status') !== 'moderated' && credential.get('projectId') === null,
        );
        break;
      case 'moderated':
        credentials = credentials.filter(
          (credential) => credential.get('status') === 'moderated' || credential.get('status') === 'moderateDeleted',
        );
        break;
      case 'project':
        credentials = credentials.filter(
          (credential) => credential.get('status') !== 'moderated' && credential.get('projectId') !== null,
        );
        break;
      default:
        break;
    }
    if (search) {
      credentials = credentials.filter((credential) => {
        if (!search) {
          return credential.get('status') !== 'moderated' && true;
        } else {
          const fullHeader =
            credential.get('name') + credential.get('login') + credential.get('link') + credential.get('projectName');
          const result = fullHeader?.toLowerCase()?.indexOf(search.toString()) !== -1;

          if (filters.get('filter') === 'moderated') {
            return result;
          } else {
            return credential.get('status') !== 'moderated' && result;
          }
        }
      });
    }
    return credentials;
  },
);

// Returns credentials with related models info
export const getCredentialsWithSummary = createSelector(
  [getCredentialByDataset, getCredentialGroups],
  (credentials, credentialGroups) => {
    return credentials.map((credential) => {
      const group = credentialGroups.get(credential.get('groupId'));
      return credential.set('groupName', group && group.get('name'));
    });
  },
);

// Returns credential with related models info
export const getCredentialSummary = createSelector(
  [getCredentialById, getCredentialGroups, getProjects, getUsers, getTeamMaps],
  (credential, credentialGroups, projects, users, teamMap) => {
    if (credential) {
      let group, project;

      if (projects && credential.get('projectId')) {
        let team;
        project = projects.get(credential.get('projectId'));

        if (teamMap && project && project.get('team')) {
          team = project
            .get('team')
            .map((id) => teamMap.get(id))
            .map((memberTeam) => memberTeam.set('user', users.get(memberTeam.get('userId'))))
            .filter((memberTeam) => memberTeam.get('user'));

          project = project.set('team', team);
        }
      }

      if (credentialGroups && credential.get('groupId')) {
        group = credentialGroups.get(credential.get('groupId'));
      }

      return credential.merge(
        fromJS({
          groupName: group && group.get('name'),
          projectName: project && project.get('name'),
          project,
        }),
      );
    }

    return null;
  },
);
